import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, X } from 'lucide-react';
import './TransactionOverlay.css';

type TransactionOverlayProps = {
  title?: string;
  subtitle?: string;
  headerless?: boolean;
  titleMono?: boolean;
  variant?: 'default' | 'wide' | 'preview';
  backLink?: { label: string; onClick: () => void };
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
};

const TransactionOverlay = ({
  title,
  subtitle,
  headerless = false,
  titleMono = false,
  variant = 'default',
  backLink,
  footer,
  onClose,
  children,
}: TransactionOverlayProps) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div
      className="transaction-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headerless ? undefined : 'transaction-overlay-title'}
    >
      <button
        type="button"
        className="transaction-overlay__backdrop"
        onClick={onClose}
        aria-label="Close"
      />
      <div
        className={[
          'transaction-overlay__panel',
          headerless ? 'transaction-overlay__panel--headerless' : '',
          variant === 'wide' ? 'transaction-overlay__panel--wide' : '',
          variant === 'preview' ? 'transaction-overlay__panel--preview' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {!headerless && (
          <div className="transaction-overlay__header">
            <div className="min-w-0 flex-1">
              {backLink ? (
                <button
                  type="button"
                  onClick={backLink.onClick}
                  className="transaction-overlay__back"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {backLink.label}
                </button>
              ) : null}
              <h2
                id="transaction-overlay-title"
                className={[
                  'transaction-overlay__title',
                  titleMono ? 'transaction-overlay__title--mono' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {title}
              </h2>
              {subtitle ? <p className="transaction-overlay__subtitle">{subtitle}</p> : null}
            </div>
            <button type="button" onClick={onClose} className="transaction-overlay__close" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div
          className={[
            'transaction-overlay__body',
            footer ? 'transaction-overlay__body--with-footer' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {children}
        </div>
        {footer ? <div className="transaction-overlay__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
};

export default TransactionOverlay;
